/**
 * Copy helper functions for Pay Rise Simulator
 * Pure functions for formatting user-facing text
 */

/**
 * Formats keep-per-pound ratio as pence
 * @param {number} keep - Keep ratio (0-1)
 * @returns {string} Formatted string like "64p per £1"
 */
export const formatKeepPerPound = (keep) => {
  const pence = Math.round(keep * 100);
  return `${pence}p per £1`;
};

/**
 * Formats effective net percentage
 * @param {number} p - Percentage as decimal (0-1)
 * @returns {string} Formatted string like "3.2% after tax"
 */
export const formatEffectiveNetPct = (p) => {
  const percentage = Math.round(p * 1000) / 10; // Round to 1 decimal place
  return `${percentage.toFixed(1)}% after tax`;
};

/**
 * Formats currency amounts with commas and no decimal places for whole amounts
 * @param {number} amount - Amount in pounds
 * @returns {string} Formatted string like "£1,234" or "£1,234.56"
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Formats percentage with 1 decimal place
 * @param {number} pct - Percentage (0-100)
 * @returns {string} Formatted string like "7.5%"
 */
export const formatPercentage = (pct) => {
  return `${(Math.round(pct * 10) / 10).toFixed(1)}%`;
};

/**
 * Creates the main copy line for percentage raises
 * @param {number} raisePct - Original raise percentage
 * @param {number} effectiveNetPct - Effective net percentage (0-1)
 * @returns {string} Copy line like "Your 5% raise becomes 3.2% after tax."
 */
export const createRaiseImpactCopy = (raisePct, effectiveNetPct) => {
  const effectiveFormatted = formatEffectiveNetPct(effectiveNetPct);
  return `Your ${formatPercentage(raisePct)} raise becomes ${effectiveFormatted}.`;
};

/**
 * Creates the keep-per-pound copy line
 * @param {number} keepPerPound - Keep ratio (0-1)
 * @returns {string} Copy line like "You keep 64p of every £1 raised."
 */
export const createKeepPerPoundCopy = (keepPerPound) => {
  const formatted = formatKeepPerPound(keepPerPound);
  return `You keep ${formatted.replace(' per £1', '')} of every £1 raised.`;
};

/**
 * Creates negotiation guidance copy
 * @param {number} requiredGrossAnnual - Required gross annual increase
 * @param {number} targetNetMonthly - Target net monthly increase
 * @returns {string} Copy line like "Ask for £4,800 gross to get £300 net."
 */
export const createNegotiationCopy = (requiredGrossAnnual, targetNetMonthly) => {
  const grossFormatted = formatCurrency(requiredGrossAnnual);
  const netFormatted = formatCurrency(targetNetMonthly);
  return `Ask for ${grossFormatted} gross to get ${netFormatted} net per month.`;
};